<script>
    import { onMount } from "svelte";
    import { preventRedirect, preventRedirectObj } from "../../stores";
    import { PageHead } from "../../Components/Common/index.svelte";
    import { Button } from "../../Components/Common/index.svelte";
    import { goBack } from "../../Utils/utilFunctions.svelte";
    import CommonDetailsSection from "./components/createMultiSections/CommonDetailsSection.svelte";
    import MultipleEncountersDetails from "./components/createMultiSections/MultipleEncountersDetails.svelte";
    import { NoteTypes } from "../../Utils/utilConstants.svelte";
    import { apiMethods } from "../../Services/index.svelte";
    import { tick } from 'svelte';
    import {
        goToURL,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";
    import {
        resetIrrelevantInsuranceDetails,
    } from "./helpers/encounterHelper.svelte";
    import {
        DirectInsuranceType,
        defaultState,
    } from "../../Utils/utilEnums.svelte";
    import NotificationModal from "../../Components/Common/NotificationModal.svelte";

    let isSaving = false;
    let patientDetailsRefs = [null];
    let insuranceDetailsRefs = [null];
    let patientContactDetailsRefs = [null];

    let commonDetails = {
        ref: null,
        selectedPreOpInstruction: null,
        selectedPreOpQuestionnaire: null,
        selectedSurgeon: null,
        selectedHospital: null,
        selectedDoctorProvider: null,
        selectedDoctor: null
    }; //used to store common details for all encounters in the list

    let emptyEncounter = {
            expanded: true,
            showContactPersonDetails: false,
            patientDetails: {
                source: "WebApp",
                sex: 2,
                alerts: "",
                notes: "",
                state: defaultState,
                patientContacts: [],
            },
            patientEncounterDetails: {
                source: "WebApp",
                procedureName: "",
                lengthInHours: 0.25,
                billingStatus: 0,
                scheduledTime: "08:00:00",
                scheduledDate: new Date(),
                neuraxialType: Number(0),
                typeOfBill: Number(0),
                notes: "",
            },
            insuranceDetails: {},
            workerCompensation: null,
            workerCompensationObj: {},
            trafficAuthority: null,
            trafficAuthorityObj: {},
            publicInPrivateObj: {},
            thirdPartyPayerObj: {},
            defenceObj: {},
            patientContactDetails: { useAsContact: false }
    };

    let showSaveWithoutValidations = false;
    // if set TRUE then no validation check on submission
    let overrideValidation = false;

    let patientEncounters = [
        structuredClone(emptyEncounter)
    ]; //used to store encounters list, start with an empty encounter

    onMount(() => {

    }); 

    const checkValidation = () => {
        let errorFields = [];

        // Check common details
        if (commonDetails.ref) {
            errorFields = [...errorFields, ...commonDetails.ref.checkValidation()];
        }

        // Check all encounters
        for (let i = 0; i < patientEncounters.length; i++) {
            // Patient details validation
            if (patientDetailsRefs[i]) {
                errorFields = [...errorFields, ...patientDetailsRefs[i].checkValidation()];
            }

            // Patient contact validation
            if (patientContactDetailsRefs[i]) {
                errorFields = [...errorFields, ...patientContactDetailsRefs[i].checkValidation()];
            }

            // Insurance details validation
            if (insuranceDetailsRefs[i]) {
                errorFields = [...errorFields, ...insuranceDetailsRefs[i].checkValidation()];
            }
        }

        // Scroll to first error field if exists
        if (errorFields.length > 0) {
            const scrollToElement = document.getElementById(errorFields[0]);
            if (scrollToElement) {
                scrollToElement.scrollIntoView({ behavior: "smooth" });
            }
        }

        return errorFields.length === 0;
    };


    $: pageHeadTitle =  "Create Multiple Patient Encounters";

    const getNextScheduledTime = () => {
        if (!patientEncounters || patientEncounters.length === 0) {
        return "08:00:00"; // Default time if list is empty
        }

        // Parse scheduledTime into Date objects (using today’s date)
        const times = patientEncounters.map(item => {
            var scheduledTime = item.patientEncounterDetails.scheduledTime;
            if(scheduledTime) {
                const [hours, minutes, seconds] = scheduledTime.split(":").map(Number);
                const date = new Date();
                date.setHours(hours, minutes, seconds || 0, 0);
                return date;
            }
        });

        // Find latest time
        const latest = new Date(Math.max(...times.map(d => d.getTime())));

        // Add 15 minutes
        latest.setMinutes(latest.getMinutes() + 15);

        // Format back to HH:mm:ss
        const pad = n => n.toString().padStart(2, "0");
        return `${pad(latest.getHours())}:${pad(latest.getMinutes())}:${pad(latest.getSeconds())}`;
    }

    const onAddEncounter = () => {
        var newEncounter = structuredClone(emptyEncounter);
        newEncounter.patientEncounterDetails.scheduledTime = getNextScheduledTime();
        //set the new encounter scheduled time based on the previous ones
        patientEncounters = [...patientEncounters, newEncounter];
        
        // Also add empty placeholders for refs
        patientDetailsRefs = [...patientDetailsRefs, null];
        insuranceDetailsRefs = [...insuranceDetailsRefs, null];
        patientContactDetailsRefs = [...patientContactDetailsRefs, null];
    };

    let deleteModalOptions = {
        show: false,
        index: null
    };

    const onRemoveEncounter = (index) => {
        deleteModalOptions = {
            show: true,
            index: index
        };
    };

    const removeEncounter = () => {
         // Remove the encounter
        if(deleteModalOptions.index != null){
            patientEncounters.splice(deleteModalOptions.index, 1);
            patientEncounters = [...patientEncounters];

            // Remove corresponding refs
            patientDetailsRefs.splice(deleteModalOptions.index, 1);
            insuranceDetailsRefs.splice(deleteModalOptions.index, 1);
            patientContactDetailsRefs.splice(deleteModalOptions.index, 1);

            // Trigger reactivity
            patientDetailsRefs = [...patientDetailsRefs];
            insuranceDetailsRefs = [...insuranceDetailsRefs];
            patientContactDetailsRefs = [...patientContactDetailsRefs];
        }

        deleteModalOptions = {
                show: false,
                index: null
        }
    }

    const save = async () => {
        patientEncounters = patientEncounters.map(item => ({ ...item, expanded: true }));
         // Wait for DOM to update
        await tick();

        if(!checkValidation()){ //not valid form, return and show save without validations
            showSaveWithoutValidations = true;
            return;
        }
        isSaving = true;

        for(let i = 0; i< patientEncounters.length; i++) {
            var details = patientEncounters[i];
            //clean irrelevant fields
             const cleanUpDataObj = resetIrrelevantInsuranceDetails(
                details.patientDetails,
                details.insuranceDetails,
                details.workerCompensation,
                details.workerCompensationObj,
                details.trafficAuthority,
                details.trafficAuthorityObj,
                details.publicInPrivateObj,
                details.thirdPartyPayerObj,
                details.defenceObj
            );

            details.patientDetails = cleanUpDataObj.patientDetails;
            details.insuranceDetails = cleanUpDataObj.insuranceDetails;
            details.workerCompensation = cleanUpDataObj.wcData;
            details.workerCompensationObj = cleanUpDataObj.wcObj;
            details.trafficAuthority = cleanUpDataObj.tacData;
            details.trafficAuthorityObj = cleanUpDataObj.tacObj;
            details.publicInPrivateObj = cleanUpDataObj.pipObj;
            details.thirdPartyPayerObj = cleanUpDataObj.tppObj;
            details.defenceObj = cleanUpDataObj.defObj;

            //create patient
            var createdPatient = await createPatient(details);
            if(createdPatient){
                //create ecounter
                await createEncounter(createdPatient, details)
            }
        }

        //after everything finished, go back to list with filters
        const urlToOpen = `/PatientEncounter/page=0&size=25&doctorId=${commonDetails.selectedDoctor.id}&fromDate=${commonDetails.scheduledDate}&toDate=${commonDetails.scheduledDate}&surgeonIDs=${commonDetails.selectedSurgeon?.providerUniqueId}`;
        console.log("Url to open:", urlToOpen);
        goToURL(urlToOpen); 

        isSaving = false;
    }

    const _updatePatientInsuranceDetails = (details) => {
        const { insuranceType } = details.patientDetails;

        if (details.patientDetails.insurance) delete details.patientDetails.insurance;
        if (details.patientDetails.insuranceId) delete details.patientDetails.insuranceId;
        if (details.patientDetails.membershipNo) delete patientDetails.membershipNo;

        switch (insuranceType) {
            case 0: // Private
                if (details.insuranceDetails.insuranceFund)
                    details.patientDetails.insuranceId =
                        details.insuranceDetails.insuranceFund.id;
                else delete details.patientDetails.insuranceId;
                details.patientDetails.membershipNo = details.insuranceDetails.membershipNo;
                details.patientDetails.membershipRefNo =
                    details.insuranceDetails.membershipRefNo
                        ? Number(details.insuranceDetails.membershipRefNo)
                        : null;
                if (details.insuranceDetails.privateCoverType)
                    details.patientDetails.insurance =
                        details.insuranceDetails.privateCoverType;
                break;
            case 10: // Overseas cover
                details.patientDetails.insurance = details.insuranceDetails.overseasCover;
                if (details.insuranceDetails.insuranceFund)
                    details.patientDetails.insuranceId =
                        details.insuranceDetails.insuranceFund.id;
                details.patientDetails.membershipNo = details.insuranceDetails.membershipNo;
                details.patientDetails.membershipRefNo =
                    details.insuranceDetails.membershipRefNo
                        ? Number(details.insuranceDetails.membershipRefNo)
                        : null;
                break;
            case 11: // Self insured
            case 15: // Cosmetic
                break;
            case 16: // Workers Comp
                details.patientDetails.insurance = `${
                    details.insuranceDetails.employer || ""
                } | ${details.insuranceDetails.employerEmail || ""} | ${
                    details.workerCompensationObj.claimNumber || ""
                }`;

                if (details.insuranceDetails.employer && details.workerCompensation)
                    details.workerCompensation.employer = details.insuranceDetails.employer;

                if (details.insuranceDetails.employerEmail && details.workerCompensation)
                    details.workerCompensation.employerEmail =
                        details.insuranceDetails.employerEmail;
                break;
            case 17: // Traffic accident
                details.patientDetails.insurance = `${details.trafficAuthorityObj.claimNumber ? details.trafficAuthorityObj.claimNumber : ""}`;
                break;
            case 20: // DVA
                details.patientDetails.insurance = `${details.insuranceDetails.dvaNumber}`;
                break;
            case 25: // Defence BUPA
            case 26: // Defence AMA
                details.patientDetails.insurance = `${details.defenceObj.pid ?? details.insuranceDetails.pidNumber} | ${details.defenceObj.dan ?? details.insuranceDetails.danNumber}`;
                break;
            case 30: // Public in Private
                details.patientDetails.insurance = `${
                    details.publicInPrivateObj.email ?? ""
                } | ${details.publicInPrivateObj.vendorNumbers ?? ""}`;
                break;
            default:
                break;
        }
    };

    const createEncounter = async (createdPatient, details) => { 
        //update encounter details
        details.patientDetails = { ...createdPatient };

        details.patientEncounterDetails = {
            ...details.patientEncounterDetails,
            patient: { id: createdPatient.id },
            doctor: { id: commonDetails.selectedDoctor.id },
            surgeon: !commonDetails.selectedSurgeon
                ? { id: 0 }
                : {
                      id: commonDetails.selectedSurgeon?.id,
                      providerUniqueId:
                          commonDetails.selectedSurgeon?.providerUniqueId,
                  },
            hospital: !commonDetails.selectedHospital
                ? { id: 0 }
                : { id: commonDetails.selectedHospital.id },
            doctorProvider: !commonDetails.selectedDoctorProvider
                ? null
                : {
                      number: commonDetails.selectedDoctorProvider.number,
                  },
            typeOfBill: commonDetails.typeOfBill,
            patientAddress: createdPatient.address,
            patientEmail: createdPatient.email,
            patientPhone: createdPatient.phone,
            patientCountry: createdPatient.country,
            patientState: createdPatient.state,
            patientCity: createdPatient.city,
            patientPostCode: createdPatient.postCode,
        };

        if (details.patientDetails.insuranceType == 16) {
            if (details.workerCompensation?.id)
                details.patientEncounterDetails.patientEncounterWorkCoverData = {
                    workCoverAuthority: details.workerCompensation?.id ?? 0,
                    email: details.workerCompensationObj?.insurerEmail
                        ? details.workerCompensationObj.insurerEmail
                        : details.workerCompensation.email,
                    claimId: details.workerCompensationObj?.claimNumber ?? "",
                    employer: details.insuranceDetails.employer ?? "",
                    employerEmail: details.insuranceDetails.employerEmail ?? "",
                };
        } else if (
            details.patientEncounterDetails.patientEncounterWorkCoverData
                ?.workCoverAuthority
        )
            delete details.patientEncounterDetails.patientEncounterWorkCoverData;

        if (details.patientDetails.insuranceType == 17) {
            if (details.trafficAuthority?.id)
                details.patientEncounterDetails.patientEncounterTrafficAccidentData = {
                    trafficAccidentAuthority: details.trafficAuthority.id,
                    email: details.trafficAuthorityObj?.email
                        ? details.trafficAuthorityObj.email
                        : details.trafficAuthority.email,
                    claimId: details.trafficAuthorityObj.claimNumber,
                };
        } else if (
            details.patientEncounterDetails.patientEncounterTrafficAccidentData
                ?.trafficAccidentAuthority
        )
            delete details.patientEncounterDetails.patientEncounterTrafficAccidentData;

        if (details.patientDetails.insuranceType == 18) {
            details.patientEncounterDetails.patientEncounterThirdPartyInsurerData = {
                ...details.thirdPartyPayerObj,
            };
        } else
            delete details.patientEncounterDetails.patientEncounterThirdPartyInsurerData;

        if (details.patientDetails.insuranceType == 30) {
            details.patientEncounterDetails.patientEncounterPIPData = {
                email: details.publicInPrivateObj.email ? details.publicInPrivateObj.email : "",
                vendorNumbers: details.publicInPrivateObj.vendorNumbers
                    ? details.publicInPrivateObj.vendorNumbers
                    : "",
            };
        } else if (details.patientEncounterDetails.patientEncounterPIPData?.email)
            delete details.patientEncounterDetails.patientEncounterPIPData;

        if (
            details.patientDetails.insuranceType === DirectInsuranceType.DefenceBupa ||
            details.patientDetails.insuranceType === DirectInsuranceType.DefenceAMA
        ) {
            
            details.patientEncounterDetails.patientEncounterDefenceData = {
                ...details.defenceObj
            };
        } else {
            delete details.patientEncounterDetails.patientEncounterDefenceData;
        }

        //create encounter
        const encounterHasNotes = details.patientEncounterDetails.notes.length > 0;

        let encounterNotes;

        if (encounterHasNotes) {
            encounterNotes = details.patientEncounterDetails.notes;
            delete details.patientEncounterDetails.notes;
        }
        try {
            var patientEncounterCreated = await apiMethods.postMethod.createPatientEncounter(details.patientEncounterDetails);
            if (patientEncounterCreated.id) {
                if (encounterHasNotes) {    
                    //create encounter note
                    await createEncounterNotes(encounterNotes, patientEncounterCreated.id);
                }
               
            } else {
                showError(res.error);
            }
        }
        catch(err) {
            showException(err)
        }
    }

    const createPatient = async (details) => {

        //if (await _checkInputFieldsValid()) {

            _updatePatientInsuranceDetails(details);

            if (details.patientDetails.title == "Title") details.patientDetails.title = 20;

            if (details.patientDetails.medicareRefNo)
                details.patientDetails.medicareRefNo = Number(
                    details.patientDetails.medicareRefNo,
                );
            else details.patientDetails.medicareRefNo = null;

            if (!details.patientDetails.alternativeFirstName)
                details.patientDetails.alternativeFirstName = "";
            if (!details.patientDetails.alternativeLastName)
                details.patientDetails.alternativeLastName = "";

            if (!details.patientEncounterDetails.scheduledTime)
                details.patientEncounterDetails.scheduledTime = "00:00:00";
            else if (
                details.patientEncounterDetails.scheduledTime.split(":").length < 3
            )
                details.patientEncounterDetails.scheduledTime += ":00";

            if (commonDetails.scheduledDate)
                details.patientEncounterDetails.scheduledDate =
                    commonDetails.scheduledDate.split("T")[0] +
                    "T" +
                    details.patientEncounterDetails.scheduledTime;

            if (!details.patientEncounterDetails.dischargeTime)
                details.patientEncounterDetails.dischargeTime = "00:00:00";
            else if (
                details.patientEncounterDetails.dischargeTime.split(":").length < 3
            )
                details.patientEncounterDetails.dischargeTime += ":00";

            if (details.patientEncounterDetails.dischargeDate)
                details.patientEncounterDetails.dischargeDate =
                    details.patientEncounterDetails.dischargeDate.split("T")[0] +
                    "T" +
                    details.patientEncounterDetails.dischargeTime;
            
            if (!details.patientDetails.medicareNo) details.patientDetails.medicareNo = "";
            if (!details.patientDetails.membershipNo) details.patientDetails.membershipNo = "";

            //create the patient
            try {
                if(details.patientDetails.id) {
                    //patient matched, update
                    var patientUpdateResponse = await apiMethods.putMethod.updatePatient(details.patientDetails);
                    if(patientUpdateResponse.id){
                         details.patientDetails.id = patientUpdateResponse.id;
                        //create contact if exist
                        if(details.patientContactDetails.name ||
                            details.patientContactDetails.relationship ||
                            details.patientContactDetails.email ||
                            details.patientContactDetails.phone) {
                                if(details.patientContactDetails.id){
                                    await _updatePatientContact(details.patientContactDetails);
                                } else {
                                    await _addPatientContact(details.patientContactDetails, details.patientDetails.id);
                                }
                        }
                        return patientUpdateResponse;   
                    } else showError(patientUpdateResponse.error);
                } else {
                    //create new
                    var patientCreateResponse = await apiMethods.postMethod.createPatient(details.patientDetails);
                    if (patientCreateResponse.id) {
                        details.patientDetails.id = patientCreateResponse.id;
                        //create contact if exist
                        if(details.patientContactDetails.name ||
                            details.patientContactDetails.relationship ||
                            details.patientContactDetails.email ||
                            details.patientContactDetails.phone) {
                                await _addPatientContact(details.patientContactDetails, details.patientDetails.id);
                        }
                        return patientCreateResponse;   
                        
                    } else showError(patientCreateResponse.error);
                }
               
            }
            catch(err){
                showException(err);
            }
            return null; //no patient created

        //}
    };

    const createEncounterNotes = async (notes, encounterID) => {
        await apiMethods.postMethod
            .createNote({
                relTo: NoteTypes.PatientEncounter,
                relId: encounterID,
                content: notes,
                visibleToDoctors: true,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create encounter notes, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showError(err));
    };

    const _addPatientContact = async (patientContactDetails, patientId) => {
        await apiMethods.postMethod
            .createPatientContact({
                ...patientContactDetails,
                patientId: patientId,
            })
            .catch((err) => showException(err));
    };

    const _updatePatientContact = async (patientContactDetails) => {
        await apiMethods.putMethod
            .updatePatientContact(patientContactDetails)
            .catch((err) => showException(err));
    };

</script>

<PageHead
    title={pageHeadTitle}
    backFunc={() => {
        if (!$preventRedirect) goBack();
        else {
            $preventRedirectObj.showModal = true;
        } 
    }} 
/> 

<div class="relative flex-1 overflow-y-auto">
    <div class="absolute inset-0 flex-1 flex flex-col gap-2">
       <div class="flex justify-end gap-x-2">
            {#if showSaveWithoutValidations}
                <Button
                    clickFunc={() => {
                        overrideValidation = true;
                        save();
                    }}
                    isDisabled={patientEncounters.length == 0 || isSaving}
                    defaultClass="btn btn-sm btn-error text-white text-sm flex-none border-none w-auto h-10 min-w-[200px] mx-2"
                    manualClass={isSaving
                        ? "loading"
                        : ""}
                >
                    {isSaving ? "Saving" : "Save with invalid fields"}
                </Button>
            {/if}
            <Button
                clickFunc={() => {
                    overrideValidation = false;
                    save();
                }}
                defaultClass="btn btn-sm btn-secondary text-white text-sm flex-none border-none w-auto h-10 min-w-[200px] mx-2"
                isDisabled={patientEncounters.length == 0 || isSaving}
                manualClass={isSaving
                    ? "loading"
                    : ""}
            >
                {isSaving ? "Saving" : "Save all encounters"}
            </Button>
       </div>
       <div>
            <CommonDetailsSection 
                bind:this={commonDetails.ref} 
                {overrideValidation}
                commonDetails={commonDetails}
                onDoctorChangeCallback={async () => {
                    for(let i=0; i< insuranceDetailsRefs.length; i++){
                        if(insuranceDetailsRefs[i] && insuranceDetailsRefs[i].onDoctorChange) {
                            await insuranceDetailsRefs[i].onDoctorChange();
                        }
                    }
                }}
                ></CommonDetailsSection>
       </div>
       <div>
            <MultipleEncountersDetails 
                bind:patientDetailsRefs={patientDetailsRefs}
                bind:insuranceDetailsRefs={insuranceDetailsRefs}
                bind:patientContactDetailsRefs={patientContactDetailsRefs}
                {overrideValidation}
                commonDetails={commonDetails}
                onAddEncounter={onAddEncounter} 
                onRemoveEncounter={onRemoveEncounter}
                patientEncounters={patientEncounters}>
            </MultipleEncountersDetails>
       </div>
        <div class="flex justify-start gap-x-2">
            <Button
                clickFunc={() => {
                    overrideValidation = false;
                    save();
                }}
                defaultClass="btn btn-sm btn-secondary text-white text-sm flex-none border-none w-auto h-10 min-w-[200px] mx-2"
                isDisabled={patientEncounters.length == 0}
                manualClass={isSaving
                    ? "loading"
                    : ""}
            >
                {isSaving ? "Saving" : "Save all encounters"}
            </Button>
            {#if showSaveWithoutValidations}
                <Button
                    clickFunc={() => {
                        overrideValidation = true;
                        save();
                    }}
                    isDisabled={patientEncounters.length == 0 || isSaving}
                    defaultClass="btn btn-sm btn-error text-white text-sm flex-none border-none w-auto h-10 min-w-[200px] mx-2"
                    manualClass={isSaving
                        ? "loading"
                        : ""}
                >
                    {isSaving ? "Saving" : "Save with invalid fields"}
                </Button>
            {/if}
       </div>
    </div>
</div>

<NotificationModal
    title="Delete Patient"
    isModalOpen={deleteModalOptions.show}
    cancelText="Cancel"
    closeModal={() => {
        deleteModalOptions.show = false;
        deleteModalOptions.index = null;
        }
    }
    submitText="Yes"
    confirmAction={removeEncounter}
    closeModalFunction={() => {
        deleteModalOptions.show = false;
        deleteModalOptions.index = null;
        }
    }
>
  Are you sure you want to delete this patient?
</NotificationModal>
